import { Button, Empty, Form, Spin, Tabs } from "antd";
import FormField from "components/atoms/FormField";
import FullHeightContainerLayout from "components/atoms/FullHeightContainerLayout";
import AttachmentTab from "components/molecules/AttachmentTab";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Header } from "semantic-ui-react";
import { getUnmaskedDataByUUIDRequest } from "store/dataObjects/actions";

const { TabPane } = Tabs;

const ReadOnlyFormBuilder = ({ selectedDataObject, schemaFields, onSave, readOnly }) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [updatedFields, setUpdatedFields] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const initialValuesRef = useRef({});
    const unmaskedData = useSelector((state) => state.dataObjects.unmaskedData);

    useEffect(() => {
        if (selectedDataObject?.dataObjectId) {
            setLoading(true);
            dispatch(getUnmaskedDataByUUIDRequest(selectedDataObject.dataObjectId));
        }
    }, [selectedDataObject, dispatch]);

    useEffect(() => {
        if (unmaskedData) {
            setLoading(false);
            const clone = _.cloneDeep(unmaskedData.dataObject) || {};
            form.setFieldsValue(clone);
            initialValuesRef.current = clone;
        }
    }, [unmaskedData, form]);

    useEffect(() => {
        if (!unmaskedData && selectedDataObject) {
            initialValuesRef.current = _.cloneDeep(selectedDataObject) || {};
        }
    }, [selectedDataObject, unmaskedData]);

    const dataToRender = unmaskedData?.dataObject || selectedDataObject;
    const fieldsToRender = unmaskedData?.schemaModel?.fields || schemaFields;

    const handleValuesChange = (changedValues, allValues) => {
        setIsEditing(true);
        const changes = {};
        Object.keys(changedValues).forEach((key) => {
            if (!_.isEqual(changedValues[key], dataToRender?.[key])) {
                changes[key] = changedValues[key];
            }
        });
        setUpdatedFields((prev) => ({ ...prev, ...changes }));
    };

    const handleSave = () => {
        if (!isEditing || Object.keys(updatedFields).length === 0) {
            return;
        }
        try {
            const currentValues = form.getFieldsValue(true) || {};
            const initial = initialValuesRef.current || {};

            const changes = {};
            Object.keys(currentValues).forEach((key) => {
                const curr = currentValues[key];
                const orig = initial[key];
                if (curr !== undefined && !_.isEqual(curr, orig)) {
                    changes[key] = curr;
                }
            });

            if (Object.keys(changes).length === 0) {
                return;
            }

            const payload = {
                ...changes,
                version: selectedDataObject?.version,
            };

            onSave(payload);
        } catch (err) {
            if (Object.keys(updatedFields).length > 0) {
                onSave({
                    ...updatedFields,
                    version: selectedDataObject?.version,
                });
            }
        }

        setIsEditing(false);
        setUpdatedFields({});
    };

    if (!dataToRender) {
        return (
            <>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Dataset record selected" />
            </>
        );
    }

    return (
        <FullHeightContainerLayout
            showHeader={false}
            header={
                <Header as="h5" dividing style={{ marginBottom: 12 }}>
                    Dataset Record
                </Header>
            }
            content={
                loading ? (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <Spin size="large" tip="Loading data..." />
                    </div>
                ) : (
                    <Tabs defaultActiveKey="record" className="h-full">
                        <TabPane tab="Record" key="record">
                            <Form
                                form={form}
                                key={dataToRender?.dataObjectId || dataToRender?.dataObject?.dataObjectId || "modal-form"}
                                name={
                                    dataToRender?.dataObjectId
                                        ? `modal-form-${dataToRender.dataObjectId}`
                                        : dataToRender?.dataObject?.dataObjectId
                                          ? `modal-form-${dataToRender.dataObject.dataObjectId}`
                                          : "modal-form"
                                }
                                layout="vertical"
                                initialValues={initialValuesRef.current}
                                className="readonly-form-builder"
                                onValuesChange={handleValuesChange}
                            >
                                {fieldsToRender?.map((field) => (
                                    <FormField key={field.fieldId} {...field} readOnly={false} />
                                ))}
                            </Form>
                        </TabPane>
                        <TabPane tab="Attachments" key="attachments">
                            <AttachmentTab
                                dataObject={dataToRender}
                                schemaFields={fieldsToRender}
                                piiProtected={!!unmaskedData}
                                onRemove={(file) => console.log("Remove attachment:", file)}
                                onDownload={(file) => console.log("Download attachment:", file)}
                            />
                        </TabPane>
                    </Tabs>
                )
            }
            footer={
                <div style={{ textAlign: "right", marginTop: "20px" }}>
                    <Button type="primary" onClick={handleSave} disabled={readOnly || !isEditing}>
                        Save
                    </Button>
                </div>
            }
        />
    );
};

export default ReadOnlyFormBuilder;
